'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import type { OrderSummary, OrderListParams } from '../../../infrastructure/OMSClient';
import { OrdersTable } from './components/OrdersTable';
import { MergeOrdersModal } from './components/MergeOrdersModal';
import { useOMS } from '../../../apps/hooks/oms/useOMS';
import { getDefaultOrderDateRange } from '../../../utils/dateUtils';
import { buildOrdersUrlParams, readOrdersUrlState, type OrdersUrlState } from './urlState';

// Stored status from a previous (label-based) build would not match the canonical API value,
// so only accept lowercase-hyphen values.
const readStoredStatus = (): string => {
  const stored = localStorage.getItem('status') || '';
  return /^[a-z-]+$/.test(stored) ? stored : '';
};

const readStoredCountry = (): string => {
  const stored = localStorage.getItem('country') || '';
  return stored === 'Select Country' ? '' : stored;
};

export default function OrdersPage() {
  const oms = useOMS();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const hasLoadedInitialPageRef = useRef(false);
  const defaultDateRange = getDefaultOrderDateRange();
  const initialUrlStateRef = useRef<OrdersUrlState | null>(null);

  if (!initialUrlStateRef.current) {
    initialUrlStateRef.current = readOrdersUrlState(searchParams, {
      startDate: localStorage.getItem('selectedStartDate') || defaultDateRange.startDate,
      endDate: localStorage.getItem('selectedEndDate') || defaultDateRange.endDate,
      country: readStoredCountry(),
      status: readStoredStatus(),
      itemsPerPage: localStorage.getItem('itemsPerPage') || '15',
      hasOrderNumber: localStorage.getItem('hasOrderNumber') === 'true',
      hasTrackingNumber: localStorage.getItem('hasTrackingNumber') === 'true',
    });
  }

  const initialUrlState = initialUrlStateRef.current;

  const [startDate, setStartDate] = useState(() => initialUrlState.startDate);
  const [endDate, setEndDate] = useState(() => initialUrlState.endDate);
  const [country, setCountry] = useState(() => initialUrlState.country);
  const [status, setStatus] = useState(() => initialUrlState.status);
  const [itemsPerPage, setItemsPerPage] = useState(() => initialUrlState.itemsPerPage);
  const [hasOrderNumber, setHasOrderNumber] = useState(() => initialUrlState.hasOrderNumber);
  const [hasTrackingNumber, setHasTrackingNumber] = useState(() => initialUrlState.hasTrackingNumber);

  const [searchValue, setSearchValue] = useState(() => initialUrlState.search);
  const [appliedSearch, setAppliedSearch] = useState(() => initialUrlState.search);
  const [picValue, setPicValue] = useState(() => initialUrlState.pic);
  const [appliedPic, setAppliedPic] = useState(() => initialUrlState.pic);

  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [prevCursors, setPrevCursors] = useState<Array<string | undefined>>(() => initialUrlState.prevCursors);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(() => initialUrlState.currentCursor);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const fetchPage = async (cursor: string | undefined, search: string, pic: string) => {
    const requestId = ++requestIdRef.current;
    const trimmedSearch = search.trim();
    const trimmedPic = pic.trim();
    setLoading(true);

    try {
      const params: OrderListParams = {
        startDate,
        endDate,
        limit: Number(itemsPerPage),
      };
      if (country) {
        params.countryCode = country;
      }
      if (status) {
        params.status = status;
      }
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      if (trimmedPic) {
        params.pic = trimmedPic;
      }
      if (hasOrderNumber) {
        params.hasOrderNumber = true;
      }
      if (hasTrackingNumber) {
        params.hasTrackingNumber = true;
      }
      if (cursor) {
        params.lastEvaluatedKey = cursor;
      }

      const response = await oms.listOrdersPage(params);
      if (requestId !== requestIdRef.current) {
        return;
      }

      setOrders(response.orders);
      setNextCursor(response.lastEvaluatedKey ? JSON.stringify(response.lastEvaluatedKey) : undefined);
      setTotalCount(response.totalCount ?? null);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      console.error('Error fetching orders:', error);
      setOrders([]);
      setNextCursor(undefined);
      setTotalCount(null);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (searchValue === appliedSearch) {
      return;
    }

    const timeout = setTimeout(() => {
      setAppliedSearch(searchValue);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [searchValue, appliedSearch]);

  useEffect(() => {
    if (picValue === appliedPic) {
      return;
    }

    const timeout = setTimeout(() => {
      setAppliedPic(picValue);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [picValue, appliedPic]);

  useEffect(() => {
    const cursor = hasLoadedInitialPageRef.current ? undefined : currentCursor;

    if (hasLoadedInitialPageRef.current) {
      setPrevCursors([]);
      setCurrentCursor(undefined);
    }
    setNextCursor(undefined);
    setTotalCount(null);
    fetchPage(cursor, appliedSearch, appliedPic);
    hasLoadedInitialPageRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, country, status, itemsPerPage, appliedSearch, appliedPic, hasOrderNumber, hasTrackingNumber]);

  useEffect(() => {
    setSearchParams(
      buildOrdersUrlParams({
        startDate,
        endDate,
        country,
        status,
        itemsPerPage,
        search: appliedSearch,
        pic: appliedPic,
        hasOrderNumber,
        hasTrackingNumber,
        currentCursor,
        prevCursors,
      }),
      { replace: true },
    );
  }, [
    startDate,
    endDate,
    country,
    status,
    itemsPerPage,
    appliedSearch,
    appliedPic,
    hasOrderNumber,
    hasTrackingNumber,
    currentCursor,
    prevCursors,
    setSearchParams,
  ]);

  useEffect(() => {
    const interval = setInterval(() => fetchPage(currentCursor, appliedSearch, appliedPic), 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    country,
    status,
    itemsPerPage,
    appliedSearch,
    appliedPic,
    currentCursor,
    hasOrderNumber,
    hasTrackingNumber,
  ]);

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    if (newStartDate !== startDate || newEndDate !== endDate) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      localStorage.setItem('selectedStartDate', newStartDate);
      localStorage.setItem('selectedEndDate', newEndDate);
    }
  };

  const handleCountryChange = (value: string) => {
    const normalized = value === 'Select Country' ? '' : value;
    setCountry(normalized);
    localStorage.setItem('country', normalized);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    localStorage.setItem('status', value);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(value);
    localStorage.setItem('itemsPerPage', value);
  };

  const handleHasOrderNumberChange = (value: boolean) => {
    setHasOrderNumber(value);
    localStorage.setItem('hasOrderNumber', String(value));
  };

  const handleHasTrackingNumberChange = (value: boolean) => {
    setHasTrackingNumber(value);
    localStorage.setItem('hasTrackingNumber', String(value));
  };

  const handleNextPage = () => {
    if (!nextCursor || loading) {
      return;
    }

    setPrevCursors((stack) => [...stack, currentCursor]);
    setCurrentCursor(nextCursor);
    fetchPage(nextCursor, appliedSearch, appliedPic);
  };

  const handlePrevPage = () => {
    if (prevCursors.length === 0 || loading) {
      return;
    }

    const stack = [...prevCursors];
    const previous = stack.pop();
    setPrevCursors(stack);
    setCurrentCursor(previous);
    fetchPage(previous, appliedSearch, appliedPic);
  };

  const handleToggleOrderSelect = (orderId: string) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId],
    );
  };

  const handleMerged = () => {
    setSelectedOrderIds([]);
    fetchPage(currentCursor, appliedSearch, appliedPic);
  };

  const selectedOrders = orders.filter((order) => selectedOrderIds.includes(order.orderId));

  return (
    <div className="w-full max-w-[80%] mx-auto py-4 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold pb-3">Order Management Dashboard</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <OrdersTable
            orders={orders}
            isLoading={loading}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            country={country}
            onCountryChange={handleCountryChange}
            status={status}
            onStatusChange={handleStatusChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
            onSearchSubmit={() => setAppliedSearch(searchValue)}
            onSearchClear={() => {
              setSearchValue('');
              setAppliedSearch('');
            }}
            picValue={picValue}
            onPicValueChange={setPicValue}
            onPicSubmit={() => setAppliedPic(picValue)}
            onPicClear={() => {
              setPicValue('');
              setAppliedPic('');
            }}
            hasOrderNumber={hasOrderNumber}
            onHasOrderNumberChange={handleHasOrderNumberChange}
            hasTrackingNumber={hasTrackingNumber}
            onHasTrackingNumberChange={handleHasTrackingNumberChange}
            pageNumber={prevCursors.length + 1}
            hasPrevPage={prevCursors.length > 0}
            hasNextPage={Boolean(nextCursor)}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            totalCount={totalCount}
            selectedOrderIds={selectedOrderIds}
            onToggleOrderSelect={handleToggleOrderSelect}
            onMergeClick={() => setShowMergeModal(true)}
          />
        </CardContent>
      </Card>

      <MergeOrdersModal
        show={showMergeModal}
        onHide={() => setShowMergeModal(false)}
        orders={selectedOrders}
        onMerged={handleMerged}
      />
    </div>
  );
}
